import {
  AlertTriangle,
  Ban,
  CheckCircle,
  Download,
  ExternalLink,
  Pencil,
} from "lucide-react";
import { usePostHog } from "@posthog/react/slim";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  validateImproveResumeOutput,
  type Aggressiveness,
  type ImproveResumeLlmOutput,
  type OptimizedResumeDocument,
  type ResumeTemplate,
} from "@super-resume-new/resume-engine";
import { Button } from "@super-resume-new/ui/components/button";
import { Spinner } from "@super-resume-new/ui/components/spinner";
import { Badge } from "@super-resume-new/ui/components/badge";
import { cn } from "@super-resume-new/ui/lib/utils";
import { client } from "@/utils/orpc";
import { CoverageScorecard } from "./coverage-scorecard";
import { DefendCards } from "./defend-cards";
import { GapQuestionnaire, type GapAnswer } from "./gap-questionnaire";
import { ResumeEditorDialog } from "./resume-editor-dialog";
import { TemplatePicker } from "./template-picker";

type Props = {
  result: ImproveResumeLlmOutput;
  defaultTemplate?: ResumeTemplate;
  aggressiveness: Aggressiveness;
  onRerun: (answers: GapAnswer[]) => void;
  rerunning: boolean;
  /**
   * The original resume text, when the browser has it (paste mode). It enables
   * the hard-stop check — a claim the source never mentions. On upload the
   * extraction happens server-side and the client never sees the text, so the
   * check is skipped here; the server logs it either way.
   */
  sourceText?: string;
  applyUrl?: string | null;
  company?: string | null;
  /**
   * The idempotency key the browser minted for this generation. It is the only
   * handle the client has on the tracker row, so hand-edits can only be persisted
   * when it is present — absent for signed-out users and when "add to tracker"
   * was off, in which case an edit stays in this session.
   */
  generationId?: string | null;
};

export function ResumeResult({
  result,
  defaultTemplate = "simple",
  aggressiveness,
  onRerun,
  rerunning,
  sourceText,
  applyUrl,
  company: _company,
  generationId,
}: Props) {
  const posthog = usePostHog();
  const [template, setTemplate] = useState<ResumeTemplate>(defaultTemplate);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const prevUrlRef = useRef<string | null>(null);

  // The live document. Starts as what the model wrote and is replaced wholesale
  // by a hand-edit; everything downstream — PDF, filename, machine checks —
  // reads this rather than `result.optimized_resume`.
  const [doc, setDoc] = useState<OptimizedResumeDocument>(
    result.optimized_resume as OptimizedResumeDocument,
  );
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [edited, setEdited] = useState(false);

  const renderPdf = useCallback(
    async (t: ResumeTemplate, d: OptimizedResumeDocument) => {
      setRendering(true);
      try {
        const res = await client.resume.renderResumePdf({ doc: d, template: t });
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        const bytes = Uint8Array.from(atob(res.base64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        prevUrlRef.current = url;
        setPdfUrl(url);
      } catch (err) {
        toast.error("PDF render failed: " + (err instanceof Error ? err.message : "Unknown error"));
      } finally {
        setRendering(false);
      }
    },
    [],
  );

  useEffect(() => {
    renderPdf(template, doc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateChange = useCallback(
    (t: ResumeTemplate) => {
      setTemplate(t);
      renderPdf(t, doc);
    },
    [renderPdf, doc],
  );

  const handleSaveEdit = useCallback(
    async (next: OptimizedResumeDocument) => {
      setSavingEdit(true);
      setDoc(next);
      setEdited(true);
      setEditing(false);
      posthog.capture("resume_manually_edited", { persisted: Boolean(generationId) });
      try {
        // Render first: the PDF the user is about to download matters more than
        // the tracker copy, and it is the visible confirmation the edit landed.
        await renderPdf(template, next);
        if (generationId) {
          const res = await client.tracker.saveEditedResume({ generationId, doc: next });
          if (!res.success) {
            toast.warning("Edit applied. It could not be saved to your job tracker.");
            return;
          }
        }
        toast.success("Resume updated.");
      } catch (err) {
        toast.warning(
          "Edit applied to this page, but saving it failed: " +
            (err instanceof Error ? err.message : "Unknown error"),
        );
      } finally {
        setSavingEdit(false);
      }
    },
    [generationId, posthog, renderPdf, template],
  );

  const handleDownload = useCallback(() => {
    if (!pdfUrl) return;
    posthog.capture("resume_pdf_downloaded", { template });
    const slug = (s: string) =>
      s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
    const name = slug(doc?.personal?.name ?? "") || "resume";
    const role = slug(doc?.headline ?? "");
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = [name, role, today].filter(Boolean).join("_") + ".pdf";
    a.click();
  }, [pdfUrl, posthog, doc, template]);

  // Machine checks (plan §4): diagnostics for us, not advice for the user — a
  // rule name like "unsourced_claim" reads as a defect in their resume. Logged
  // against the live document so a hand-edit that reintroduces a problem shows up.
  const issues = useMemo(
    () => validateImproveResumeOutput({ ...result, optimized_resume: doc }, sourceText),
    [result, doc, sourceText],
  );

  useEffect(() => {
    if (issues.length > 0) console.warn("[resume checks]", issues);
  }, [issues]);

  // Streamed partials can arrive before these fields exist, so read them defensively
  // even though the schema marks them required.
  const blockers = result.blockers ?? [];
  const warnings = result.warnings ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* 1. Hard requirements you may not meet & Warnings section (appears FIRST, one after the other) */}
      {(blockers.length > 0 || warnings.length > 0) && (
        <div className="flex flex-col gap-3">
          {blockers.length > 0 && (
            <section className="flex flex-col gap-2.5">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
                <Ban size={16} className="shrink-0" aria-hidden="true" />
                Hard requirements you may not meet ({blockers.length})
              </h3>
              <p className="text-[11px] text-muted-foreground leading-snug">
                This posting states requirements your resume does not satisfy, and extra detail will
                not clear them. Each one quotes the posting. Read them before you spend time applying.
              </p>
              <div className="flex flex-col gap-2">
                {blockers.map((b, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-destructive/30 bg-destructive/[0.06] p-3 text-xs font-medium text-destructive leading-relaxed flex items-start gap-2 shadow-xs"
                  >
                    <Ban className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {warnings.length > 0 && (
            <section className="flex flex-col gap-2.5">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-amber-500">
                <AlertTriangle size={16} className="text-amber-500 shrink-0" aria-hidden="true" />
                Warnings ({warnings.length})
              </h3>
              <div className="flex flex-col gap-2">
                {warnings.map((w, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs font-medium text-amber-500 leading-relaxed flex items-start gap-2 shadow-xs"
                  >
                    <AlertTriangle className="size-4 text-amber-500 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* 2. Keyword coverage (left) and Changes made (right) side by side */}
      {((result.coverage &&
        ((result.coverage.found?.length ?? 0) > 0 || (result.coverage.missing?.length ?? 0) > 0)) ||
        (result.changes && result.changes.length > 0)) && (
        <div
          className={cn(
            "grid gap-6 items-stretch",
            result.changes &&
              result.changes.length > 0 &&
              result.coverage &&
              ((result.coverage.found?.length ?? 0) > 0 ||
                (result.coverage.missing?.length ?? 0) > 0)
              ? "grid-cols-1 lg:grid-cols-2"
              : "grid-cols-1",
          )}
        >
          <CoverageScorecard
            coverage={result.coverage}
            target_profile={result.target_profile}
            className="h-full"
          />

          {result.changes && result.changes.length > 0 && (
            <section className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm h-full">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                  <CheckCircle size={16} className="text-emerald-500" />
                  Changes made ({result.changes.length})
                </h3>
                <span className="text-xs text-muted-foreground">Tailoring summary</span>
              </div>
              <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto pr-1 flex-1">
                {result.changes.map((c, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-border/80 bg-muted/40 p-2.5 shadow-xs flex flex-col gap-1"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="info" className="text-[10px] py-0 px-2">
                        {c.section}
                      </Badge>
                    </div>
                    <p className="text-xs font-medium text-foreground leading-relaxed">{c.change}</p>
                    <p className="text-[11px] text-muted-foreground leading-snug">{c.reason}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* 3. Choose template (left, 2 col) and Preview (right, more width) side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Template Selection */}
        <div className="lg:col-span-4 xl:col-span-4 rounded-xl border border-border bg-card p-4 shadow-sm flex flex-col gap-3">
          <TemplatePicker
            selected={template}
            onChange={handleTemplateChange}
            disabled={rendering}
          />
        </div>

        {/* Right Column: Resume Preview & Actions */}
        <section className="lg:col-span-8 xl:col-span-8 flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">Preview</h3>
              {edited && (
                <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                  Manually edited
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                disabled={rendering || savingEdit}
                className="gap-1.5 cursor-pointer shadow-sm"
              >
                <Pencil className="size-3.5" aria-hidden="true" />
                Edit
              </Button>
              {applyUrl && (
                <a href={applyUrl} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    size="sm"
                    className="font-semibold gap-1.5 cursor-pointer shadow-sm"
                  >
                    Apply to Job
                    <ExternalLink className="size-3.5" />
                  </Button>
                </a>
              )}
              <Button
                id="download-pdf-btn"
                size="sm"
                onClick={handleDownload}
                disabled={!pdfUrl || rendering}
                loading={rendering}
              >
                {!rendering && <Download aria-hidden="true" />}
                {rendering ? "Rendering…" : "Download PDF"}
              </Button>
            </div>
          </div>

          <div className="flex h-[720px] w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
            {rendering && !pdfUrl && (
              <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
                <Spinner className="size-8 text-muted-foreground" />
                <p>Rendering PDF…</p>
              </div>
            )}
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                title="Resume PDF preview"
                className="h-full w-full border-none rounded-xl"
              />
            )}
          </div>
        </section>
      </div>

      <ResumeEditorDialog
        open={editing}
        onOpenChange={setEditing}
        doc={doc}
        onSave={handleSaveEdit}
        saving={savingEdit}
      />

      {/* Defend cards only make sense for stretched claims, i.e. level 3. */}
      {aggressiveness === "aggressive" && <DefendCards assignments={result.keyword_assignments} />}

      <GapQuestionnaire questions={result.gap_questions} onRerun={onRerun} busy={rerunning} />
    </div>
  );
}
